import { defineStore } from 'pinia';
import { fetchNewQuestions } from '../services/questionService';
import { submitFeedback, submitSuggestion as submitSuggestionService } from '../services/feedbackService';
import { logAction } from '../services/logService';
import { trackEvent, trackScreen } from '../services/analyticsService';
import { logHandledError } from '../services/errorTrackingService';

export const useStore = defineStore('main', {
    state: () => ({
        question: {},
        deeperRevealed: false,
        allQuestions: [],
        seenQuestions: new Set(),
        questionType: 'truth',
        showQuestionTypeButtons: true,
        gameStarted: false,
        omitAdultQuestions: true,
        lastFetchDate: null,
        loadingQuestions: false,
        liked: false,
        disliked: false,
        questionCount: 0,
        showDonationScreen: false,
        hasShownDonation: false,
        showSuggestionModal: false,
        suggestionText: '',
        modalType: 'suggestion', // 'suggestion' or 'feedback'
        isMenuOpen: false,
        showGameRules: false,
        showPrivacyPolicy: false,
        darkMode: false,
        isSubmitting: false,
        showToast: false,
        toastMessage: '',
        toastTimeout: null,
    }),
    getters: {
        filteredQuestions: (state) => {
            if (state.omitAdultQuestions) {
                return state.allQuestions.filter(q => q.adult === false);
            }
            return state.allQuestions;
        },
    },
    actions: {
        // Hamburger Menu Methods
        toggleMenu() {
            this.isMenuOpen = !this.isMenuOpen;
        },
        openGameRules() {
            this.toggleMenu();
            this.showGameRules = true;
            this.showSuggestionModal = false;
            this.showDonationScreen = false;
            this.showPrivacyPolicy = false;
            trackScreen('game_rules');
        },
        openSuggestionForm() {
            this.toggleMenu();
            this.modalType = 'suggestion';
            console.log('Opening suggestion form, modalType set to:', this.modalType);
            this.showSuggestionModal = true;
            this.showGameRules = false;
            this.showDonationScreen = false;
            this.showPrivacyPolicy = false;
            trackScreen('suggestion_form');
        },
        openFeedbackForm() {
            this.toggleMenu();
            this.modalType = 'feedback';
            console.log('Opening feedback form, modalType set to:', this.modalType);
            this.showSuggestionModal = true;
            this.showGameRules = false;
            this.showDonationScreen = false;
            this.showPrivacyPolicy = false;
            trackScreen('feedback_form');
        },
        openDonation() {
            this.toggleMenu();
            this.showDonationScreen = true;
            this.showSuggestionModal = false;
            this.showGameRules = false;
            this.showPrivacyPolicy = false;
        },
        openPrivacyPolicy() {
            this.toggleMenu();
            this.showPrivacyPolicy = true;
            this.showSuggestionModal = false;
            this.showGameRules = false;
            this.showDonationScreen = false;
            trackScreen('privacy_policy');
        },

        // Dark Mode Toggle
        toggleDarkMode() {
            this.darkMode = !this.darkMode;
            this.applyDarkMode();
            localStorage.setItem('darkMode', JSON.stringify(this.darkMode));
            trackEvent('dark_mode_toggled', {
                enabled: this.darkMode
            });
        },
        applyDarkMode() {
            if (this.darkMode) {
                document.body.classList.add('dark-mode');
            } else {
                document.body.classList.remove('dark-mode');
            }
        },

        // Game Logic
        async startGame() {
            this.gameStarted = true;
            await this.loadQuestions();
            logAction('start_game', {
                omit_adult: this.omitAdultQuestions
            });
            trackEvent('game_started', {
                adult_mode: !this.omitAdultQuestions,
                total_questions: this.allQuestions.length
            });
            trackScreen('game');
        },
        goDeeper() {
            this.deeperRevealed = !this.deeperRevealed;
            if (this.deeperRevealed) {
                trackEvent('go_deeper_clicked', {
                    question_id: this.question.id,
                    question_type: this.question.type
                });
            }
        },
        async likeQuestion() {
            if (this.liked) return;
            this.liked = true;

            try {
                const type = this.disliked ? 'like_undo_dislike' : 'like';
                await submitFeedback(this.question.id, type);
                await logAction('like_question', {
                    question_id: this.question.id,
                    question_type: this.question.type,
                    previous_feedback: this.disliked ? 'dislike' : 'none'
                });
                trackEvent('question_liked', {
                    question_id: this.question.id,
                    question_type: this.question.type,
                    changed_mind: this.disliked
                });
                this.disliked = false;
            } catch (error) {
                logHandledError(error, { action: 'like_question', question_id: this.question.id });
                this.showToastMessage('There was an error submitting your feedback. Please try again.');
                this.liked = false; // Reset on error
            }
        },
        async dislikeQuestion() {
            if (this.disliked) return;
            this.disliked = true;

            try {
                const type = this.liked ? 'dislike_undo_like' : 'dislike';
                await submitFeedback(this.question.id, type);
                await logAction('dislike_question', {
                    question_id: this.question.id,
                    question_type: this.question.type,
                    previous_feedback: this.liked ? 'like' : 'none'
                });
                trackEvent('question_disliked', {
                    question_id: this.question.id,
                    question_type: this.question.type,
                    changed_mind: this.liked
                });
                this.liked = false;
            } catch (error) {
                logHandledError(error, { action: 'dislike_question', question_id: this.question.id });
                this.showToastMessage('There was an error submitting your feedback. Please try again.');
                this.disliked = false; // Reset on error
            }
        },
        nextQuestion() {
            this.liked = false;
            this.disliked = false;
            this.deeperRevealed = false;
            this.showQuestionTypeButtons = true;

            this.questionCount++;
            trackEvent('next_question', {
                question_count: this.questionCount,
                previous_type: this.question.type
            });
            if (this.questionCount === 5 && !this.hasShownDonation) {
                this.showDonationScreen = true;
                this.hasShownDonation = true;
                localStorage.setItem('hasShownDonation', 'true');
                trackScreen('donation');
                trackEvent('donation_screen_shown', {
                    question_count: this.questionCount
                });
            }
        },
        closeDonationScreen() {
            this.showDonationScreen = false;
            trackEvent('donation_screen_closed');
        },
        toggleAdultQuestions() {
            this.omitAdultQuestions = !this.omitAdultQuestions;
            localStorage.setItem('omitAdultQuestions', JSON.stringify(this.omitAdultQuestions));
            trackEvent('adult_mode_toggled', {
                enabled: !this.omitAdultQuestions
            });
        },

        async loadQuestions() {
            console.log('Loading questions...');
            this.loadingQuestions = true;
            this.loadQuestionsFromLocalStorage();
            console.log('Questions from localStorage:', this.allQuestions.length);

            const hasLocalQuestions = this.allQuestions.length > 0;

            if (!hasLocalQuestions) {
                console.log('No questions in localStorage, loading defaults...');
                await this.loadDefaultQuestions();
                console.log('Questions after loading defaults:', this.allQuestions.length);
            }

            // If we have local questions, allow the game to start immediately
            if (hasLocalQuestions) {
                this.loadingQuestions = false;
                console.log('Local questions available, fetching updates in background...');
            }

            // Fetch new questions (in background if we already have local questions)
            this.loadLastFetchDate();
            this.fetchAndMergeNewQuestions();

            // If we didn't have local questions, we need to wait for defaults to be ready
            if (!hasLocalQuestions) {
                this.loadingQuestions = false;
            }
            console.log('Question loading complete. Total questions:', this.allQuestions.length);
        },

        async fetchAndMergeNewQuestions() {
            try {
                const newQuestions = await fetchNewQuestions(this.lastFetchDate);
                if (newQuestions && newQuestions.length > 0) {
                    const uniqueNewQuestions = newQuestions.filter(
                        newQ => !this.allQuestions.some(existingQ => existingQ.id === newQ.id)
                    );

                    if (uniqueNewQuestions.length > 0) {
                        this.allQuestions = [...this.allQuestions, ...uniqueNewQuestions];
                        this.saveQuestionsToLocalStorage();
                        this.lastFetchDate = new Date().toISOString();
                        this.saveLastFetchDate();
                        console.log('Background fetch complete. Added', uniqueNewQuestions.length, 'new questions.');
                    }
                }
            } catch (error) {
                console.log('Background question fetch failed:', error.message);
            }
        },

        async loadDefaultQuestions() {
            try {
                console.log('Fetching default questions from /questions.json');
                const response = await fetch('/questions.json');
                console.log('Fetch response status:', response.status, response.ok);

                if (response.ok) {
                    const defaultQuestions = await response.json();
                    console.log('Default questions loaded:', defaultQuestions.length);
                    this.allQuestions = defaultQuestions.sort(() => Math.random() - 0.5);
                    this.saveQuestionsToLocalStorage();
                    console.log('Default questions saved to localStorage');
                } else {
                    console.error('Failed to fetch default questions. Status:', response.status);
                    this.showToastMessage('Failed to load questions. Please check your connection.');
                }
            } catch (error) {
                console.error('Error loading default questions:', error);
                logHandledError(error, { action: 'load_default_questions' });
                this.showToastMessage('Error loading questions. Please refresh the app.');
            }
        },
        loadQuestionsFromLocalStorage() {
            const storedQuestions = localStorage.getItem('questions');
            if (storedQuestions) {
                this.allQuestions = JSON.parse(storedQuestions).sort(() => Math.random() - 0.5);
            }
        },
        saveQuestionsToLocalStorage() {
            localStorage.setItem('questions', JSON.stringify(this.allQuestions));
        },
        loadLastFetchDate() {
            const storedDate = localStorage.getItem('lastFetchDate');
            if (storedDate) {
                this.lastFetchDate = storedDate;
            }
        },
        saveLastFetchDate() {
            localStorage.setItem('lastFetchDate', this.lastFetchDate);
        },
        setQuestionType(type) {
            console.log('Setting question type to:', type);
            console.log('Filtered questions available:', this.filteredQuestions.length);
            console.log('Seen questions:', this.seenQuestions.size);

            this.questionType = type;
            const availableQuestions = this.filteredQuestions.filter(
                q => !this.seenQuestions.has(q.id) && q.type === this.questionType
            );

            console.log('Available questions of type', type + ':', availableQuestions.length);

            const nextAvailableQuestion = availableQuestions[0];

            if (nextAvailableQuestion) {
                this.question = nextAvailableQuestion;
                this.seenQuestions.add(this.question.id);
                this.showQuestionTypeButtons = false;
                console.log('Selected question:', this.question.id, this.question.question.substring(0, 50) + '...');
                logAction('select_question_type', {
                    type: this.questionType,
                    question_id: this.question.id
                });
                trackEvent('question_type_selected', {
                    type: this.questionType,
                    question_id: this.question.id,
                    has_deeper_question: !!this.question.deeper_question,
                    is_adult: this.question.adult
                });
            } else {
                console.log('No available questions found for type:', type);
                this.showToastMessage("You've seen all the questions of this type.");
            }
        },

        // Suggestion Modal
        openSuggestionModal() {
            this.showSuggestionModal = true;
        },
        closeSuggestionModal() {
            if (this.isSubmitting) return;
            this.showSuggestionModal = false;
            this.suggestionText = '';
        },
        async submitSuggestion() {
            if (!this.suggestionText.trim() || this.isSubmitting) {
                return;
            }

            this.isSubmitting = true;
            try {
                const suggestionType = this.modalType === 'suggestion' ? 'question' : 'feedback';
                console.log('Submitting suggestion with type:', suggestionType);
                await submitSuggestionService(this.suggestionText, suggestionType);
                await logAction('submit_suggestion', {
                    suggestion_length: this.suggestionText.length,
                    type: suggestionType
                });
                trackEvent(suggestionType === 'question' ? 'suggestion_submitted' : 'feedback_submitted', {
                    text_length: this.suggestionText.length,
                    type: suggestionType
                });
                this.suggestionText = '';
                this.showSuggestionModal = false;
                this.showToastMessage(this.modalType === 'suggestion' ? 'Thank you for your question suggestion!' : 'Thank you for your feedback!');
            } catch (error) {
                logHandledError(error, { action: 'submit_suggestion', type: suggestionType });
                this.showToastMessage('There was an error submitting your suggestion. Please try again.');
            } finally {
                this.isSubmitting = false;
            }
        },

        showToastMessage(message, duration = 3000) {
            if (this.toastTimeout) {
                clearTimeout(this.toastTimeout);
            }
            this.toastMessage = message;
            this.showToast = true;
            this.toastTimeout = setTimeout(() => {
                this.showToast = false;
                setTimeout(() => {
                    this.toastMessage = '';
                }, 300);
            }, duration);
        },
        initialize() {
            this.loadingQuestions = true;
            this.loadQuestions();
            this.hasShownDonation = localStorage.getItem('hasShownDonation') === 'true';
            const storedOmitAdult = localStorage.getItem('omitAdultQuestions');
            if (storedOmitAdult !== null) {
                this.omitAdultQuestions = JSON.parse(storedOmitAdult);
            }
            // Load dark mode preference
            const storedDarkMode = localStorage.getItem('darkMode');
            if (storedDarkMode !== null) {
                this.darkMode = JSON.parse(storedDarkMode);
            }
            // Apply dark mode on initialization
            this.applyDarkMode();
        }
    }
});
